//---------------------------------------------------------
// LB rule form logic (pure, unit-tested in lbRuleEditLogic.test.ts)
//---------------------------------------------------------
import {isValidIPAddress} from 'common';
import {t} from 'i18next';
import {IServiceArguments, IServiceConfiguration} from 'types/load_balancer';

export const PROBE_PORT_ERROR = 'Enter a port from 1 to 65535.';
export const FIX_FIELDS_MESSAGE = 'Fix the highlighted fields to update this rule.';
export const TIMEOUT_ONLY_BLOCKED = "LoxiLB won't apply a change to only the timeout or retries. Change the probe type or port as well, or cancel.";
export const PROBE_RULE_ONLY_WARNING = 'This updates the rule only. The {{n}} existing endpoints keep their current health check for now.';
export const PROBE_RULE_ONLY_WARNING_ONE = 'This updates the rule only. The existing endpoint keeps its current health check for now.';
export const ENDPOINT_PROBE_MISMATCH = 'Endpoint health checks still use {{type}} {{port}}.';
export const ENDPOINT_PROBE_MISMATCH_NO_PORT = 'Endpoint health checks still use {{type}}.';
/** Mismatch row text; the port is dropped when it is 0 or missing (e.g. ping). */
export const endpointProbeMismatchText = (m: {type: string; port: number | string}) =>
	!m.port || Number(m.port) === 0 ? t(ENDPOINT_PROBE_MISMATCH_NO_PORT, {type: m.type}) : t(ENDPOINT_PROBE_MISMATCH, m);
/** List-row text for LoxiLB's inactiveTimeOut, e.g. "Idle timeout 30s"; undefined when LoxiLB omitted it. */
export const idleTimeoutLabel = (v: number | null | undefined) => (v === null || v === undefined ? undefined : t('Idle timeout {{n}}s', {n: v}));
export const probeRuleOnlyWarning = (n: number) => (n === 1 ? t(PROBE_RULE_ONLY_WARNING_ONE) : t(PROBE_RULE_ONLY_WARNING, {n}));

/**
 * The first endpoint health check (GET /config/endpoint/all, keyed by hostName =
 * endpoint IP) whose probe type/port differs from the rule's, or null.
 */
export function endpointProbeMismatch(
	sa: {probetype?: string; probeport?: number} | undefined,
	ruleEndpoints: {endpointIP?: string}[] | undefined,
	endpointChecks: {hostName?: string; probeType?: string; probePort?: number}[] | undefined,
): {type: string; port: number | string} | null {
	const ips = new Set((ruleEndpoints ?? []).map(ep => ep.endpointIP));
	const rt = norm(sa?.probetype === 'none' ? '' : sa?.probetype);
	const rp = norm(sa?.probeport);
	for (const c of endpointChecks ?? []) {
		if (!ips.has(c.hostName)) continue;
		if (norm(c.probeType === 'none' ? '' : c.probeType) !== rt || norm(c.probePort) !== rp) return {type: c.probeType || 'none', port: c.probePort ?? ''};
	}
	return null;
}
export const PROBE_NOT_SET = 'Not set on this rule.';
export const PROBE_KEYS = ['probetype', 'probeport', 'probereq', 'proberesp', 'probeTimeout', 'probeRetries'] as const;
/** True when the loaded rule carries no probe fields at all (-1 sentinels count as unset). */
export const ruleHasNoProbe = (sa: Record<string, unknown> | undefined) =>
	PROBE_KEYS.every(k => sa?.[k] === undefined || sa?.[k] === null || sa?.[k] === '' || sa?.[k] === -1 || sa?.[k] === 0);
export const NO_NAME = 'No name';
export const PORT_PROBE_TYPES = ['tcp', 'udp', 'sctp'];

export const updateFailedMessage = (status: number | string | undefined) =>
	t("Couldn't update the rule. LoxiLB returned {{status}}.", {status: status ?? 'an error'});

const isPort = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= 65535;

/** Field-keyed validation errors. Name is not required in edit mode (it is locked). */
export function validateLBForm(formData: IServiceConfiguration, isEdit: boolean): Record<string, string> {
	const e: Record<string, string> = {};
	const sa = formData.serviceArguments;

	if (!isEdit && !sa?.name?.trim()) e.name = t('Service Name is required');

	if (!sa?.externalIP?.trim()) e.externalIP = t('External IP is required');
	else if (!isValidIPAddress(sa.externalIP.trim())) e.externalIP = t('Enter a valid IP address');

	if (!isPort(sa?.port)) e.port = t('Port must be between 1 and 65535');

	if (sa?.portMax !== undefined && sa.portMax !== null && (sa.portMax as number) !== 0) {
		if (!isPort(sa.portMax)) e.portMax = t('Port Max must be between 1 and 65535');
		else if (isPort(sa?.port) && (sa.portMax as number) < (sa.port as number)) e.portMax = t('Port Max must be greater than or equal to Port Min');
	}

	if (sa?.mode === 4 && sa?.protocol && sa.protocol !== 'tcp') e.protocol = t('Full-proxy mode requires the TCP protocol');

	// Any probe port that is typed is sent, so it must be 1-65535 whatever the
	// Probe Type (None included). An empty field never blocks.
	const pp = sa?.probeport as unknown;
	if (pp !== undefined && pp !== null && pp !== '' && !isPort(Number(pp))) e.probeport = t(PROBE_PORT_ERROR);

	const eps = formData.endpoints ?? [];
	if (eps.length === 0) e.endpoints = t('At least one endpoint is required');
	else eps.forEach((ep, i) => {
		for (const [field, msg] of Object.entries(endpointFieldErrors(ep))) e[`endpoints.${i}.${field}`] = msg;
	});
	return e;
}

/** Inline errors for one endpoint row, keyed by IEndpoint field. */
export function endpointFieldErrors(ep: {endpointIP?: string; targetPort?: unknown; weight?: unknown}): Record<string, string> {
	const e: Record<string, string> = {};
	if (!ep.endpointIP?.trim() || !isValidIPAddress(ep.endpointIP.trim())) e.endpointIP = t('Enter a valid IP address');
	if (!isPort(Number(ep.targetPort))) e.targetPort = t(PROBE_PORT_ERROR);
	if (!(Number(ep.weight) >= 1)) e.weight = t('Weight must be 1 or more');
	return e;
}

// Fields LoxiLB re-applies without any other change being needed.
const TIMEOUT_KEYS = ['probeTimeout', 'probeRetries', 'inactiveTimeOut'];
// Service fields LoxiLB compares to decide a re-POST changed the rule
// (rules.go 1731-1737 plus the security/egress checks after it). Endpoints
// and allowedSources are compared separately.
const RULE_CHANGE_KEYS = ['probetype', 'probeport', 'probereq', 'proberesp', 'sel', 'mode', 'proxyprotocolv2', 'security', 'egress'];
// '', null, undefined and 0 all mean "not set" to LoxiLB (omitempty); numeric strings equal numbers.
const norm = (v: unknown): unknown => {
	if (v === undefined || v === null || v === '' || v === 0 || v === false) return null;
	if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v) || null;
	return v;
};
const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const epKey = (eps: unknown[] | undefined) => JSON.stringify((eps ?? []).map((ep: any) => [ep?.endpointIP, Number(ep?.targetPort), Number(ep?.weight)]));

/**
 * True when the edit changes a timeout/retry field and nothing LoxiLB uses to
 * detect a rule change. Upstream LoxiLB (rules.go 1731-1757) answers such a
 * re-POST with lbrule-exists and does not apply it. Fields the dialog fills
 * in on mount (oper, path_match_mode, backend_protocol, mtls_frontend) are
 * not part of LoxiLB's comparison, so they no longer hide the warning.
 */
export function onlyTimeoutOrRetriesChanged(orig: Partial<IServiceConfiguration> | undefined, next: IServiceConfiguration): boolean {
	if (!orig) return false;
	const o = (orig.serviceArguments ?? {}) as unknown as Record<string, unknown>;
	const n = (next.serviceArguments ?? {}) as unknown as Record<string, unknown>;
	if (RULE_CHANGE_KEYS.some(k => norm(o[k]) !== norm(n[k]))) return false;
	if (epKey(orig.endpoints) !== epKey(next.endpoints)) return false;
	if (!same(orig.allowedSources ?? [], next.allowedSources ?? [])) return false;
	return TIMEOUT_KEYS.some(k => norm(o[k]) !== norm(n[k]));
}

const PROBE_SETTING_KEYS = ['probetype', 'probeport'];
/** True when the edit changes the rule's probe type or port (issue #72: existing endpoint probes keep theirs). */
export function probeSettingsChanged(orig: Partial<IServiceConfiguration> | undefined, next: IServiceConfiguration): boolean {
	if (!orig) return false;
	const o = (orig.serviceArguments ?? {}) as unknown as Record<string, unknown>;
	const n = (next.serviceArguments ?? {}) as unknown as Record<string, unknown>;
	return PROBE_SETTING_KEYS.some(k => norm(o[k]) !== norm(n[k]));
}

/** Strip client-only keys; keep every service argument (probe fields included). */
export function buildUpdatePayload(data: IServiceConfiguration & {isValid?: boolean; errors?: unknown}): IServiceConfiguration {
	const {isValid, errors, ...serviceConfig} = data;
	return serviceConfig as IServiceConfiguration;
}

export type {IServiceArguments};
